import useEndChildAccountUserSession from '@/api/mutations/childAccounts/useEndChildAccountUserSession';
import useGetMe from '@/api/queries/me/useGetMe';
import { clearLocalStorage } from '@/helpers/localStorageKeyHelper';
import useCopyToClipboard from '@/hooks/useCopyToClipboard';
import { useIsPayacaAdmin } from '@/utils/customHooks';
import { Button, Tooltip } from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import { FC } from 'react';
import { useHistory } from 'react-router';

const useSessionStatus = () => {
  const { data: me } = useGetMe();
  const hasActiveSession =
    me &&
    me.user.originalAccountId &&
    me.user.account.id !== me.user.originalAccountId;
  return {
    hasActiveSession,
  };
};
const SessionStatusBanner: FC = () => {
  const history = useHistory();

  const { data: me, refetch: refetchMe } = useGetMe();
  const {
    mutateAsync: endChildAccountUserSession,
    isPending: isEndingChildAccountUserSession,
  } = useEndChildAccountUserSession();
  const { hasActiveSession } = useSessionStatus();
  const isPayacaAdmin = useIsPayacaAdmin();
  const { handleCopy } = useCopyToClipboard();

  const endAccountSession = async () => {
    if (!isEndingChildAccountUserSession) {
      await endChildAccountUserSession();
      clearLocalStorage();
      window.location.reload();
    }
  };

  const adminBaseUrl: string | undefined = import.meta.env.VITE_ADMIN_URL;

  const copyLinkToClipboard = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!me || !adminBaseUrl) return;
    const currentUrl = window.location.href;
    const manageAccountUrl = `${adminBaseUrl}/accounts/${me.user.account.id}?redirect=${encodeURIComponent(currentUrl)}`;
    await handleCopy(manageAccountUrl);
  };

  if (!me || !hasActiveSession) {
    return null;
  }
  return (
    <div
      className="flex items-center justify-between bg-orange-400 p-3 cursor-pointer"
      onClick={() => {
        history.push(`/accounts/${me.user.account.id}`);
      }}
    >
      <span>
        You are currently signed into <strong>{me.user.account.name}</strong>
      </span>
      <div className="flex items-center gap-2">
        {isPayacaAdmin && adminBaseUrl && (
          <Tooltip tooltipContent="Copy link for a colleague to access this account and page">
            <Button
              onClick={copyLinkToClipboard}
              aria-label="Copy link to clipboard"
            >
              <UntitledIcon name="copy-07.3" className="h-4 w-4" />
            </Button>
          </Tooltip>
        )}
        <Button
          size={'sm'}
          isProcessing={isEndingChildAccountUserSession}
          onClick={async (e) => {
            e.stopPropagation();
            await endAccountSession();
          }}
        >
          End session
        </Button>
      </div>
    </div>
  );
};

export default SessionStatusBanner;
